import { useEffect, useState } from "react";
import { Shield, Crown, Infinity as InfinityIcon, Zap } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

/**
 * AccountStatusBadge — shows Role · Tier · AI Quota at a glance.
 *
 * Renders inline next to the logo on desktop, and as a compact pill on mobile.
 * Polls /api/billing/me once on mount + every 60s + after a global "billing:refresh"
 * event so other components can force a refresh after an AI call.
 */
export default function AccountStatusBadge() {
  const { user } = useAuth();
  const [billing, setBilling] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const fetchOnce = () => {
      Promise.resolve().then(async () => {
        if (cancelled) return;
        try {
          const r = await api.get("/billing/me");
          if (!cancelled) setBilling(r.data);
        } catch { /* silent */ }
      });
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 60_000);
    window.addEventListener("billing:refresh", fetchOnce);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("billing:refresh", fetchOnce);
    };
  }, [user]);

  if (!user) return null;

  const isAdmin = !!user.is_admin;
  const tier = billing?.effective_tier || user.tier || "free";
  const quota = billing?.quota || {};
  const exempt = !!quota.exempt;
  const used = quota.used_last_30_days ?? null;
  const limit = quota.limit;
  const unlimited = exempt || quota.unlimited === true || limit == null;

  const tierLabel = { free: "Free", pro: "Pro", clinic: "Clinic" }[tier] || tier;
  const tierBg = isAdmin
    ? "bg-violet-500/15 text-violet-300 border-violet-500/40"
    : tier === "pro" || tier === "clinic"
    ? "bg-sage-500/15 text-sage-700 border-sage-500/30"
    : "bg-paper-100 text-ink-700 border-paper-200";

  const quotaText = unlimited
    ? "Unlimited"
    : (used != null && limit != null)
    ? `${Math.max(0, limit - used)}/${limit} left`
    : "—";

  return (
    <div className="relative shrink-0" data-testid="account-status-badge">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`min-h-[36px] sm:min-h-[40px] px-2.5 sm:px-3 rounded-full border text-xs font-medium inline-flex items-center gap-1.5 transition-all ${tierBg}`}
        title={`${isAdmin ? "Admin · " : ""}${tierLabel}${unlimited ? " · Unlimited AI" : ` · ${quotaText}`}`}
        data-testid="account-status-badge-btn"
      >
        {isAdmin ? <Shield className="w-3.5 h-3.5" strokeWidth={1.6} /> : tier !== "free" ? <Crown className="w-3.5 h-3.5" strokeWidth={1.6} /> : <Zap className="w-3.5 h-3.5" strokeWidth={1.6} />}
        <span className="hidden sm:inline">{isAdmin ? "Admin" : tierLabel}</span>
        <span className="inline-flex items-center gap-1 opacity-80">
          {unlimited ? <InfinityIcon className="w-3 h-3" strokeWidth={2} /> : null}
          <span className="hidden sm:inline">{unlimited ? "Unlimited" : quotaText}</span>
          <span className="sm:hidden">{unlimited ? "∞" : (used != null && limit != null ? `${Math.max(0, limit - used)}` : "")}</span>
        </span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 w-72 bg-white border border-paper-200 rounded-2xl shadow-xl p-4 text-sm" data-testid="account-status-popover">
          <div className="space-y-2.5">
            <Row label="Email" value={user.email || "—"} />
            <Row label="Role" value={isAdmin ? "Admin" : "Therapist"} valueClassName={isAdmin ? "text-violet-700 font-medium" : ""} />
            <Row label="Subscription" value={tierLabel} valueClassName={tier !== "free" ? "text-sage-700 font-medium" : ""} />
            <Row label="AI quota (last 30d)" value={
              unlimited
                ? "Unlimited"
                : (used != null && limit != null)
                ? `${used}/${limit} used (${Math.max(0, limit - used)} remaining)`
                : "—"
            } />
            {exempt && quota.exempt_reason && (
              <Row label="Quota bypass" value={quota.exempt_reason} valueClassName="text-violet-700 font-medium capitalize" />
            )}
            {billing?.subscription?.status && (
              <Row label="Subscription status" value={billing.subscription.status} valueClassName="capitalize" />
            )}
            {billing?.early_adopter?.granted && (
              <Row label="Early Adopter" value={`Lifetime ${billing.early_adopter.tier || "pro"}`} valueClassName="text-violet-700" />
            )}
          </div>
          <div className="mt-3 pt-3 border-t border-paper-100 text-[11px] text-ink-500 leading-relaxed">
            Auto-refreshes every minute. Admin accounts and Early Adopter pro grants are exempt from quota.
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, valueClassName = "" }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-[10px] uppercase tracking-[0.16em] text-ink-500 pt-0.5">{label}</span>
      <span className={`text-right text-ink-900 ${valueClassName}`}>{value}</span>
    </div>
  );
}
